/**
 * Boomfection Landing — Game Effects Module
 * Confetti, screen shake, explosions, kill feed, pill notifications, share image, combo counter
 * @copyright reDim GmbH | @link https://www.redim.de | @contact team@redim.de
 */

import { t } from '/js/i18n.js';

/**
 * Full-screen canvas confetti effect for victory
 */
export function showConfetti() {
    const confettiCanvas = document.createElement('canvas');
    confettiCanvas.id = 'confetti-canvas';
    confettiCanvas.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:9999;';
    document.body.appendChild(confettiCanvas);
    const ctx = confettiCanvas.getContext('2d');
    confettiCanvas.width = window.innerWidth;
    confettiCanvas.height = window.innerHeight;

    const particles = [];
    const colors = ['#00ff88', '#ff0044', '#00ccff', '#ffcc00', '#ff44cc', '#44ff44'];
    for (let i = 0; i < 120; i++) {
        particles.push({
            x: Math.random() * confettiCanvas.width,
            y: -20 - Math.random() * 200,
            w: 4 + Math.random() * 6,
            h: 8 + Math.random() * 8,
            color: colors[Math.floor(Math.random() * colors.length)],
            vx: (Math.random() - 0.5) * 4,
            vy: 2 + Math.random() * 4,
            rotation: Math.random() * 360,
            rotSpeed: (Math.random() - 0.5) * 10
        });
    }

    let frame = 0;
    function animate() {
        ctx.clearRect(0, 0, confettiCanvas.width, confettiCanvas.height);
        let alive = false;
        for (const p of particles) {
            p.x += p.vx;
            p.y += p.vy;
            p.vy += 0.05;
            p.rotation += p.rotSpeed;
            if (p.y < confettiCanvas.height + 50) alive = true;
            ctx.save();
            ctx.translate(p.x, p.y);
            ctx.rotate((p.rotation * Math.PI) / 180);
            ctx.fillStyle = p.color;
            ctx.globalAlpha = Math.max(0, 1 - frame / 180);
            ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
            ctx.restore();
        }
        frame++;
        if (alive && frame < 180) requestAnimationFrame(animate);
        else confettiCanvas.remove();
    }
    requestAnimationFrame(animate);
}

/**
 * Triggers a screen shake effect on the canvas container
 * @param {number} intensity - shake intensity multiplier
 */
export function triggerScreenShake(intensity = 1) {
    const container = document.getElementById('canvas-container');
    if (!container) return;
    const duration = 300 + intensity * 100;
    const maxOffset = 2 + intensity * 2;
    const start = performance.now();
    const shake = (now) => {
        const elapsed = now - start;
        if (elapsed > duration) {
            container.style.transform = '';
            return;
        }
        const decay = 1 - elapsed / duration;
        const x = (Math.random() - 0.5) * maxOffset * decay;
        const y = (Math.random() - 0.5) * maxOffset * decay;
        container.style.transform = `translate(${x}px, ${y}px)`;
        requestAnimationFrame(shake);
    };
    requestAnimationFrame(shake);
}

/**
 * Handles explosion visual/audio effects during chain reactions
 * @param {Object} data - explosion event data
 * @param {boolean} isChainTick - whether this is a chain tick explosion
 * @param {Object} ctx - shared context { client, soundManager, chainExplosionCount, updateComboCounter }
 */
export function handleExplosionEffect(data, isChainTick, ctx, fieldId) {
    if (!ctx.soundManager) return;
    ctx.chainExplosionCount++;
    const chainExplosionCount = ctx.chainExplosionCount;

    // Haptic feedback for explosions (respects prefers-reduced-motion)
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches && navigator.vibrate) {
        if (chainExplosionCount >= 5) {
            navigator.vibrate([100, 50, 100, 50, 100]);
        } else if (chainExplosionCount >= 3) {
            navigator.vibrate([50, 30, 50]);
        } else {
            navigator.vibrate(50);
        }
    }

    // Use escalating chain sound if available, otherwise fall back to standard
    if (typeof ctx.soundManager.playExplosionChain === 'function') {
        ctx.soundManager.playExplosionChain(chainExplosionCount - 1);
    } else {
        const volumeMultipliers = [1.0, 0.8, 0.65, 0.55, 0.5, 0.45];
        const volume = volumeMultipliers[Math.min(chainExplosionCount - 1, volumeMultipliers.length - 1)];
        ctx.soundManager.playExplosion(chainExplosionCount, { volume });
    }
    if (chainExplosionCount >= 2) {
        let ownerColor;
        if (isChainTick) {
            const field = ctx.client.game?.sphere.getField(fieldId ?? data.explosions?.[0]);
            const owner = field ? ctx.client.game?.players.get(field.ownerId) : null;
            ownerColor = owner?.color?.hex;
        } else {
            const owner = ctx.client.game?.players.get(data.owner);
            ownerColor = owner?.color?.hex;
        }
        updateComboCounter(chainExplosionCount, ownerColor);
    }
    if (chainExplosionCount >= 5) {
        triggerScreenShake(Math.min(chainExplosionCount - 4, 5));
        if (ctx.client.renderer?.zoomTo) {
            ctx.client.renderer.zoomTo(2.5);
            setTimeout(() => {
                if (ctx.client.renderer?.zoomTo) ctx.client.renderer.zoomTo(3.2);
            }, 2000);
        }
    }
}

/**
 * Shows a kill feed entry when a player is eliminated
 * @param {string} killerName
 * @param {string} victimName
 * @param {string} killerColor - CSS color
 */
export function showKillFeed(killerName, victimName, killerColor) {
    let feed = document.getElementById('kill-feed');
    if (!feed) {
        feed = document.createElement('div');
        feed.id = 'kill-feed';
        feed.style.cssText =
            'position:fixed;top:80px;left:20px;z-index:1000;display:flex;flex-direction:column;gap:6px;pointer-events:none;';
        document.body.appendChild(feed);
    }
    const entry = document.createElement('div');
    entry.style.cssText = `background:rgba(20,10,40,0.9);border-left:3px solid ${killerColor || '#ff0044'};padding:6px 12px;border-radius:4px;color:#fff;font-size:0.85rem;animation:slideInLeft 0.3s ease;`;
    entry.textContent = t('game.killfeed', { killer: killerName, victim: victimName });
    feed.appendChild(entry);
    setTimeout(() => {
        entry.style.opacity = '0';
        entry.style.transition = 'opacity 0.5s';
        setTimeout(() => entry.remove(), 500);
    }, 4000);
}

/** Pill type descriptions for notifications and tooltips */
export const pillDescriptions = {
    neutralizer: { get name() { return t('pill.neutralizer.name'); }, get effect() { return t('pill.neutralizer.effect'); } },
    mutation1: { get name() { return t('pill.mutation1.name'); }, get effect() { return t('pill.mutation1.effect'); } },
    mutation2: { get name() { return t('pill.mutation2.name'); }, get effect() { return t('pill.mutation2.effect'); } },
    mutation3: { get name() { return t('pill.mutation3.name'); }, get effect() { return t('pill.mutation3.effect'); } },
    swallow: { get name() { return t('pill.swallow.name'); }, get effect() { return t('pill.swallow.effect'); } },
    boost: { get name() { return t('pill.boost.name'); }, get effect() { return t('pill.boost.effect'); } },
    freeze: { get name() { return t('pill.freeze.name'); }, get effect() { return t('pill.freeze.effect'); } },
    sniper: { get name() { return t('pill.sniper.name'); }, get effect() { return t('pill.sniper.effect'); } },
    shuffle: { get name() { return t('pill.shuffle.name'); }, get effect() { return t('pill.shuffle.effect'); } },
    magnet: { get name() { return t('pill.magnet.name'); }, get effect() { return t('pill.magnet.effect'); } },
    poison: { get name() { return t('pill.poison.name'); }, get effect() { return t('pill.poison.effect'); } },
    mystery: { get name() { return t('pill.mystery.name'); }, get effect() { return t('pill.mystery.effect'); } },
    shield: { get name() { return t('pill.shield.name'); }, get effect() { return t('pill.shield.effect'); } },
    leech: { get name() { return t('pill.leech.name'); }, get effect() { return t('pill.leech.effect'); } },
    swap: { get name() { return t('pill.swap.name'); }, get effect() { return t('pill.swap.effect'); } }
};

/**
 * Shows a pill activation notification
 * @param {string} pillType
 * @param {string} _playerName - unused, kept for signature compatibility
 */
export function showPillNotification(pillType, _playerName) {
    const notification = document.getElementById('pill-notification');
    if (!notification) return;
    const pillInfo = pillDescriptions[pillType] || { name: pillType, effect: '' };
    notification.className = 'pill-notification';
    notification.classList.add(pillType);
    notification.querySelector('.pill-type').textContent = pillInfo.name;
    notification.querySelector('.pill-effect').textContent = pillInfo.effect;
    notification.classList.add('visible');
    setTimeout(() => notification.classList.remove('visible'), 2500);
}

/**
 * Generates a share image canvas with game results
 * @param {string} winnerName
 * @param {string|number} mapSize
 * @param {string|number} longestChain
 * @param {string} color - CSS color
 * @returns {HTMLCanvasElement}
 */
export function generateShareImage(winnerName, mapSize, longestChain, color) {
    const canvas = document.createElement('canvas');
    canvas.width = 600;
    canvas.height = 300;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#0a0a1e';
    ctx.fillRect(0, 0, 600, 300);
    ctx.strokeStyle = color || '#00ff88';
    ctx.lineWidth = 3;
    ctx.strokeRect(5, 5, 590, 290);
    ctx.fillStyle = '#00ff88';
    ctx.font = 'bold 28px monospace';
    ctx.textAlign = 'center';
    ctx.fillText('BOOMFECTION', 300, 50);
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 36px monospace';
    ctx.fillText(t('game.winnerAnnounce', { name: winnerName }), 300, 120);
    ctx.fillStyle = '#aaaaaa';
    ctx.font = '18px monospace';
    ctx.fillText(`${t('game.mapLabel')}: ${mapSize} | ${t('game.longestChain')}: ${longestChain}`, 300, 170);
    ctx.fillText('boomfection.de', 300, 260);
    return canvas;
}

/** @private combo hide timeout handle */
let comboHideTimeout = null;

/**
 * Updates the combo counter UI element
 * @param {number} count - current chain count
 * @param {string} playerColor - CSS color for the combo
 */
export function updateComboCounter(count, playerColor) {
    const comboEl = document.getElementById('combo-counter');
    if (!comboEl) return;
    const numberEl = comboEl.querySelector('.combo-number');
    const textEl = comboEl.querySelector('.combo-text');

    let text = t('comboChain');
    let cssClass = '';
    if (count >= 100) {
        text = t('comboPerfection');
        cssClass = 'combo-perfection';
    } else if (count >= 90) {
        text = t('comboTranscendent');
        cssClass = 'combo-transcendent';
    } else if (count >= 80) {
        text = t('comboInfinite');
        cssClass = 'combo-infinite';
    } else if (count >= 70) {
        text = t('comboMythical');
        cssClass = 'combo-mythical';
    } else if (count >= 60) {
        text = t('comboLegendary');
        cssClass = 'combo-legendary';
    } else if (count >= 50) {
        text = t('comboCatastrophe');
        cssClass = 'combo-catastrophe';
    } else if (count >= 40) {
        text = t('comboApocalypse');
        cssClass = 'combo-apocalypse';
    } else if (count >= 35) {
        text = t('comboDomination');
        cssClass = 'combo-domination';
    } else if (count >= 30) {
        text = t('comboUnstoppable');
        cssClass = 'combo-unstoppable';
    } else if (count >= 25) {
        text = t('comboGodlike');
        cssClass = 'combo-godlike';
    } else if (count >= 20) {
        text = t('comboInsane');
        cssClass = 'combo-insane';
    } else if (count >= 15) {
        text = t('comboRampage');
        cssClass = 'combo-rampage';
    } else if (count >= 12) {
        text = t('comboMonster');
        cssClass = 'combo-monster';
    } else if (count >= 10) {
        text = t('comboUltra');
        cssClass = 'combo-ultra';
    } else if (count >= 8) {
        text = t('comboAmazing');
        cssClass = 'combo-amazing';
    } else if (count >= 6) {
        text = t('comboSuper');
        cssClass = 'combo-super';
    } else if (count >= 5) {
        text = t('comboMega');
        cssClass = 'combo-mega';
    } else if (count >= 4) {
        text = t('comboGreat');
        cssClass = 'combo-great';
    } else if (count >= 3) {
        text = t('comboTriple');
        cssClass = 'combo-triple';
    } else if (count >= 2) {
        text = t('comboDouble');
        cssClass = 'combo-double';
    }

    numberEl.textContent = `${count}x`;
    textEl.textContent = text;
    comboEl.style.setProperty('--combo-color', playerColor || '#ff8c32');
    comboEl.className = 'combo-counter';
    if (cssClass) comboEl.classList.add(cssClass);
    comboEl.classList.remove('hidden');

    numberEl.style.animation = 'none';
    numberEl.offsetHeight;
    numberEl.style.animation = null;

    if (count === 5 || count === 10 || count === 15) {
        const canvasContainer = document.getElementById('canvas-container');
        if (canvasContainer) {
            canvasContainer.classList.add('screen-shake');
            setTimeout(() => canvasContainer.classList.remove('screen-shake'), 400);
        }
    }

    clearTimeout(comboHideTimeout);
    comboHideTimeout = setTimeout(() => comboEl.classList.add('hidden'), 2000);
}
